class WholesaleBookingForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.messageContainer = this.form.querySelector('.form-message');
    this.sheetId = this.dataset.scriptUrl || '';
    this.successMessage = this.dataset.successMessage || 'Thank you! Your booking request has been submitted successfully.';
    this.errorMessage = this.dataset.errorMessage || 'Something went wrong. Please try again.';
    
    this.validationRules = {
      email: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address'
      },
      phone: {
        pattern: /^[\d\s\-\+\(\)]+$/,
        message: 'Please enter a valid phone number',
        minLength: 10
      },
      text: {
        minLength: 2,
        message: 'This field must be at least 2 characters'
      }
    };
    
    this.init();
  }
  
  init() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.setupRealTimeValidation();
    this.setupDateConstraints();
  }
  
  setupRealTimeValidation() {
    const inputs = this.form.querySelectorAll('input, textarea, select');
    
    inputs.forEach(input => {
      input.addEventListener('blur', () => {
        if (input.value.trim() !== '' || input.hasAttribute('required')) {
          this.validateField(input);
        }
      });
      
      input.addEventListener('focus', () => {
        this.clearFieldError(input);
      });
      
      if (input.type === 'email' || input.type === 'tel') {
        input.addEventListener('input', this.debounce(() => {
          if (input.value.trim() !== '') {
            this.validateField(input);
          }
        }, 500));
      }
    });
  }
  
  setupDateConstraints() {
    const dateInputs = this.form.querySelectorAll('input[type="date"]');
    const today = new Date().toISOString().split('T')[0];
    
    dateInputs.forEach(input => {
      if (!input.hasAttribute('min')) {
        input.setAttribute('min', today);
      }
    });
  }
  
  validateField(field) {
    const fieldContainer = field.closest('.form-field');
    const errorElement = fieldContainer.querySelector('.form-error');
    const fieldType = field.type;
    const fieldValue = field.value.trim();
    
    this.clearFieldError(field);
    
    if (field.hasAttribute('required') && fieldValue === '') {
      this.showFieldError(field, 'This field is required');
      return false;
    }
    
    if (fieldValue === '') {
      return true;
    }
    
    if (fieldType === 'email') {
      if (!this.validationRules.email.pattern.test(fieldValue)) {
        this.showFieldError(field, this.validationRules.email.message);
        return false;
      }
    }
    
    if (fieldType === 'tel') {
      const digitsOnly = fieldValue.replace(/\D/g, '');
      if (!this.validationRules.phone.pattern.test(fieldValue)) {
        this.showFieldError(field, this.validationRules.phone.message);
        return false;
      }
      if (digitsOnly.length < this.validationRules.phone.minLength) {
        this.showFieldError(field, `Phone number must be at least ${this.validationRules.phone.minLength} digits`);
        return false;
      }
    }
    
    if (fieldType === 'text' && field.hasAttribute('minlength')) {
      const minLength = parseInt(field.getAttribute('minlength'));
      if (fieldValue.length < minLength) {
        this.showFieldError(field, `This field must be at least ${minLength} characters`);
        return false;
      }
    }
    
    if (field.hasAttribute('pattern')) {
      const pattern = new RegExp(field.getAttribute('pattern'));
      if (!pattern.test(fieldValue)) {
        this.showFieldError(field, field.getAttribute('title') || 'Please match the requested format');
        return false;
      }
    }
    
    if (fieldType === 'date') {
      const selectedDate = new Date(fieldValue);
      const minDate = field.getAttribute('min') ? new Date(field.getAttribute('min')) : null;
      const maxDate = field.getAttribute('max') ? new Date(field.getAttribute('max')) : null;
      
      if (minDate && selectedDate < minDate) {
        this.showFieldError(field, 'Please select a valid date');
        return false;
      }
      
      if (maxDate && selectedDate > maxDate) {
        this.showFieldError(field, 'Please select a valid date');
        return false;
      }
    }
    
    return true;
  }
  
  showFieldError(field, message) {
    const fieldContainer = field.closest('.form-field');
    const errorElement = fieldContainer.querySelector('.form-error');
    
    field.classList.add('form-input--error');
    field.setAttribute('aria-invalid', 'true');
    errorElement.textContent = message;
    errorElement.style.display = 'block';
  }
  
  clearFieldError(field) {
    const fieldContainer = field.closest('.form-field');
    const errorElement = fieldContainer.querySelector('.form-error');
    
    field.classList.remove('form-input--error');
    field.removeAttribute('aria-invalid');
    errorElement.textContent = '';
    errorElement.style.display = 'none';
  }
  
  validateForm() {
    const inputs = this.form.querySelectorAll('input, textarea, select');
    let isValid = true;
    let firstInvalidField = null;
    
    inputs.forEach(input => {
      if (!this.validateField(input)) {
        isValid = false;
        if (!firstInvalidField) {
          firstInvalidField = input;
        }
      }
    });
    
    if (firstInvalidField) {
      firstInvalidField.focus();
    }
    
    return isValid;
  }
  
  async handleSubmit(event) {
    event.preventDefault();
    
    if (!this.validateForm()) {
      this.showMessage('Please fix the errors above', 'error');
      return;
    }
    
    this.setSubmitState(true);

    const formData = new FormData(this.form);
    const data = {};
    
    formData.forEach((value, key) => {
      data[key] = value;
    });
    
    data.timestamp = new Date().toISOString();
    
    try {
      await this.submitToGoogleSheets(formData);
      this.showMessage(this.successMessage, 'success');
      this.form.reset();
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          'event': 'form_submission',
          'form_name': 'wholesale_booking'
        });
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      this.showMessage(this.errorMessage, 'error');
    } finally {
      this.setSubmitState(false);
    }
  }
  
  async submitToGoogleSheets(data) {
    if (!this.sheetId) {
      throw new Error('Google Sheet ID is not configured.');
      return;
    }
    const url = this.sheetId;
    const response = await fetch(url, {
      method: 'POST',
      body: data
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text);
    }

    return response.json();
  }

  setSubmitState(isSubmitting) {
    const buttonText = this.submitButton.querySelector('.button-text');
    const buttonLoader = this.submitButton.querySelector('.button-loader');
    const buttonIcon = this.submitButton.querySelector('.button-icon');
    
    this.submitButton.disabled = isSubmitting;
    
    if (isSubmitting) {
      this.submitButton.classList.add('button--loading');
      if (buttonText) buttonText.style.opacity = '0';
      if (buttonIcon) buttonIcon.style.opacity = '0';
      if (buttonLoader) buttonLoader.style.display = 'flex';
    } else {
      this.submitButton.classList.remove('button--loading');
      if (buttonText) buttonText.style.opacity = '1';
      if (buttonIcon) buttonIcon.style.opacity = '1';
      if (buttonLoader) buttonLoader.style.display = 'none';
    }
  }
  
  showMessage(message, type) {
    this.messageContainer.textContent = message;
    this.messageContainer.className = `form-message form-message--${type}`;
    this.messageContainer.style.display = 'block';
    this.messageContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (type === 'success') {
      setTimeout(() => {
        this.messageContainer.style.display = 'none';
      }, 5000);
    }
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define('wholesale-booking-form', WholesaleBookingForm);